// 全局复用的 AI 任务 WebSocket 客户端（精简版，只负责建立连接并在控制台输出状态）
// 连接地址：wss://server.zxplanet.com.cn/api/ws/ai-task?token={accessToken}

class AiTaskWebSocket {
  constructor (token) {
    this.token = token
    this.ws = null
    this.heartbeatTimer = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.isManualClose = false
    // 简单的订阅者列表，用于把消息分发给各个页面
    this.listeners = []
  }

  // 更新 token（例如登录成功或刷新 token 后调用）
  setToken (token) {
    this.token = token
  }

  // 建立 WebSocket 连接（只做最简单的连接 + 控制台日志）
  connect () {
    this.isManualClose = false

    if (!this.token) {
      console.warn('[AiTaskWebSocket] 当前没有 token，跳过 WebSocket 连接')
      return
    }

    // 避免重复连接
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('[AiTaskWebSocket] WebSocket 已处于连接中或已连接状态，跳过本次连接')
      return
    }

    const wsUrl = `wss://server.zxplanet.com.cn/api/ws/ai-task?token=${encodeURIComponent(this.token)}`
    console.log('[AiTaskWebSocket] 准备连接 WebSocket：', wsUrl)

    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      console.log('[AiTaskWebSocket] WebSocket 连接成功')
      this.startHeartbeat()
      this.reconnectAttempts = 0
    }

    this.ws.onclose = () => {
      console.log('[AiTaskWebSocket] WebSocket 连接关闭')
      this.stopHeartbeat()

      // 非手动关闭且还有重试次数时，尝试重连
      if (!this.isManualClose) {
        this.tryReconnect()
      }
    }

    this.ws.onerror = () => {
      console.error('[AiTaskWebSocket] WebSocket 连接错误')
    }

    // 这里只是为了调试，简单打印收到的原始消息内容
    this.ws.onmessage = (event) => {
      console.log('[AiTaskWebSocket] 收到消息：', event.data)

      let payload = event.data
      try {
        payload = JSON.parse(event.data)
      } catch (e) {
        // 保留原始字符串
      }

      // 将消息分发给所有监听者
      if (Array.isArray(this.listeners) && this.listeners.length > 0) {
        this.listeners.forEach(fn => {
          if (typeof fn === 'function') {
            try {
              fn(payload)
            } catch (err) {
              console.error('[AiTaskWebSocket] 消息监听回调执行异常', err)
            }
          }
        })
      }
    }
  }

  // 启动心跳：每 30 秒发送一次 ping
  startHeartbeat () {
    if (this.heartbeatTimer) return

    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send('ping')
          // console.log('[AiTaskWebSocket] 发送心跳 ping')
        } catch (e) {
          console.error('[AiTaskWebSocket] 发送心跳失败', e)
        }
      }
    }, 30000)
  }

  // 停止心跳
  stopHeartbeat () {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  // 尝试断线重连
  tryReconnect () {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[AiTaskWebSocket] 已达到最大重连次数，停止重连')
      return
    }

    this.reconnectAttempts += 1
    const delay = 3000 // 简单固定 3 秒间隔
    console.log(
      `[AiTaskWebSocket] ${delay / 1000} 秒后尝试第 ${this.reconnectAttempts} 次重连`
    )

    setTimeout(() => {
      this.connect()
    }, delay)
  }

  // 主动关闭连接（可选使用）
  disconnect () {
    if (this.ws) {
      this.isManualClose = true
      this.stopHeartbeat()
      this.ws.close()
      this.ws = null
      console.log('[AiTaskWebSocket] 已主动关闭 WebSocket 连接')
    }
  }

  // 允许外部注册 / 取消注册消息监听
  addListener (fn) {
    if (typeof fn !== 'function') return
    if (!this.listeners.includes(fn)) {
      this.listeners.push(fn)
    }
  }

  removeListener (fn) {
    if (!this.listeners || this.listeners.length === 0) return
    this.listeners = this.listeners.filter(item => item !== fn)
  }
}

// --- 单例封装：整个应用只维护一条连接（对现有代码保持兼容） ---
let aiTaskWsInstance = null

export function getAiTaskWebSocket () {
  if (!aiTaskWsInstance) {
    let token = null
    try {
      const raw = window.localStorage.getItem('planetUserInfo')
      if (raw) {
        const parsed = JSON.parse(raw)
        token = parsed && (parsed.accessToken || parsed.token)
      }
    } catch (e) {
      token = null
    }
    aiTaskWsInstance = new AiTaskWebSocket(token)
  }
  return aiTaskWsInstance
}

// 根据当前本地 token 立即尝试建立连接（登录成功或应用初始化时调用）
export function ensureAiTaskWsConnected () {
  const instance = getAiTaskWebSocket()

  // 每次调用时从本地刷新一次 token，保证与最新登录状态一致
  try {
    const raw = window.localStorage.getItem('planetUserInfo')
    if (raw) {
      const parsed = JSON.parse(raw)
      const latestToken = parsed && (parsed.accessToken || parsed.token)
      instance.setToken(latestToken)
    }
  } catch (e) {}

  instance.connect()
  return instance
}

export default {
  getAiTaskWebSocket,
  ensureAiTaskWsConnected
}
